import React, {useMemo, useState, useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import ReportsDataGrid from '../../../component/right-panel/ReportDataGrid';
import {ColumnConfig} from '../../../config/reportTypes';
import {v4} from 'uuid';
import {GridSortModel} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import './GroupedAssignmentTables.scss';

interface GroupedAssignmentTablesProps {
  assignmentProgressList: any[];
  summaryColumns: ColumnConfig[];
  detailsColumns: ColumnConfig[];
  getRowId?: (row: any) => string | number;
  showDetailsTable?: boolean; // Controls visibility of Details Table based on 'include assignment detail' toggle
}

const DEFAULT_PAGE_SIZE = 10;

type PaginationState = {
  page: number;
  pageSize: number;
};

const GroupedAssignmentTables: React.FC<GroupedAssignmentTablesProps> = ({
  assignmentProgressList,
  summaryColumns,
  detailsColumns,
  getRowId,
  showDetailsTable = true, // Default to true to maintain backward compatibility
}) => {
  const [summaryPagination, setSummaryPagination] = useState<
    Record<string, PaginationState>
  >({});
  const [detailsPagination, setDetailsPagination] = useState<
    Record<string, PaginationState>
  >({});
  const [summarySortModel, setSummarySortModel] = useState<
    Record<string, GridSortModel>
  >({});
  const [detailsSortModel, setDetailsSortModel] = useState<
    Record<string, GridSortModel>
  >({});

  // Initialize pagination state for all groups when data changes
  useEffect(() => {
    if (!Array.isArray(assignmentProgressList)) return;

    const newSummaryPagination: Record<string, PaginationState> = {};
    const newDetailsPagination: Record<string, PaginationState> = {};

    assignmentProgressList.forEach((_, index) => {
      const groupKey = `group-${index}`;
      // Only initialize if not already set (preserve user's pagination state)
      if (!summaryPagination[groupKey]) {
        newSummaryPagination[groupKey] = {
          page: 1,
          pageSize: DEFAULT_PAGE_SIZE,
        };
      }
      if (!detailsPagination[groupKey]) {
        newDetailsPagination[groupKey] = {
          page: 1,
          pageSize: DEFAULT_PAGE_SIZE,
        };
      }
    });

    if (Object.keys(newSummaryPagination).length > 0) {
      setSummaryPagination((prev) => ({...prev, ...newSummaryPagination}));
    }
    if (Object.keys(newDetailsPagination).length > 0) {
      setDetailsPagination((prev) => ({...prev, ...newDetailsPagination}));
    }
  }, [assignmentProgressList]);

  // Helper function to sort data based on sortModel
  const sortData = (data: any[], sortModel: GridSortModel): any[] => {
    if (!sortModel || sortModel.length === 0) return data;

    return [...data].sort((a, b) => {
      for (const sort of sortModel) {
        const {field, sort: sortDirection} = sort;
        if (!sortDirection) continue;

        let aValue = a[field];
        let bValue = b[field];

        // Handle null/undefined values
        if (aValue == null && bValue == null) continue;
        if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
        if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

        // Convert to comparable values
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
        }
        if (typeof bValue === 'string') {
          bValue = bValue.toLowerCase();
        }

        // Compare values
        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
  };

  const groupedData = useMemo(() => {
    if (!Array.isArray(assignmentProgressList)) return [];
    return assignmentProgressList.map((progress, index) => {
      const groupKey = `group-${index}`;

      // First table data: reviewProgress_AssignmentGroup_List
      const summaryData: any[] = [];
      if (Array.isArray(progress.reviewProgress_AssignmentGroup_List)) {
        progress.reviewProgress_AssignmentGroup_List.forEach((item: any) => {
          summaryData.push({
            id: `${v4()}_summary_${item.assignmentID || item.assignmentsID || item.classificationID}`,
            ...item,
          });
        });
      }

      // Second table data: assignmentDetailsList
      const detailsData: any[] = [];
      if (Array.isArray(progress.assignmentDetailsList)) {
        progress.assignmentDetailsList.forEach((item: any) => {
          detailsData.push({
            id: `${v4()}_detail_${item.assignmentID || item.assignmentsID}`,
            ...item,
          });
        });
      }

      // Get sort models for this group
      const currentSummarySortModel = summarySortModel[groupKey] || [];
      const currentDetailsSortModel = detailsSortModel[groupKey] || [];

      // Sort data before pagination
      const sortedSummaryData = sortData(summaryData, currentSummarySortModel);
      const sortedDetailsData = sortData(detailsData, currentDetailsSortModel);

      // Get pagination state for this group
      const summaryPage = summaryPagination[groupKey]?.page || 1;
      const summaryPageSize =
        summaryPagination[groupKey]?.pageSize || DEFAULT_PAGE_SIZE;
      const detailsPage = detailsPagination[groupKey]?.page || 1;
      const detailsPageSize =
        detailsPagination[groupKey]?.pageSize || DEFAULT_PAGE_SIZE;

      // Calculate pagination slice for summary table (after sorting)
      const summaryStartIndex = (summaryPage - 1) * summaryPageSize;
      const summaryEndIndex = summaryStartIndex + summaryPageSize;
      const paginatedSummaryData = sortedSummaryData.slice(
        summaryStartIndex,
        summaryEndIndex
      );

      // Calculate pagination slice for details table (after sorting)
      const detailsStartIndex = (detailsPage - 1) * detailsPageSize;
      const detailsEndIndex = detailsStartIndex + detailsPageSize;
      const paginatedDetailsData = sortedDetailsData.slice(
        detailsStartIndex,
        detailsEndIndex
      );

      return {
        groupKey,
        groupName: progress.assignmentGroupName || 'Unknown Group',
        summaryData: paginatedSummaryData,
        summaryTotal: sortedSummaryData.length,
        detailsData: paginatedDetailsData,
        detailsTotal: sortedDetailsData.length,
      };
    });
  }, [
    assignmentProgressList,
    summaryPagination,
    detailsPagination,
    summarySortModel,
    detailsSortModel,
  ]);

  if (groupedData.length === 0) {
    return null;
  }

  return (
    <Box className="grouped-assignment-tables-container">
      {groupedData.map((group, groupSerialNo) => (
        <Box key={group.groupKey}>
          <Box
            className={`assignment-group-header ${groupSerialNo == 0 ? 'first-group' : ''}`}
          >
            <Typography
              className="assignment-group-header-title typo-text subhead3_size"
              data-testid={`grouped-assignment-tables-assignment-group-name`}
            >
              {t('reports.reviewProgressAssignment.assignmentGroup')}:{' '}
              {group.groupName}
            </Typography>
          </Box>

          {/* First Table: reviewProgress_AssignmentGroup_List */}
          <Box className="summary-table-wrapper">
            {group.summaryTotal > 0 ? (
              <ReportsDataGrid
                loading={false}
                columns={[
                  {
                    field: 'serialNo',
                    headerName: '',
                    width: 40,
                    flex: 0,
                    isVisible: true,
                  },
                  ...summaryColumns,
                ]}
                rows={group.summaryData.map((row: any, idx: number) => {
                  const currentPage =
                    summaryPagination[group.groupKey]?.page || 1;
                  const pageSize =
                    summaryPagination[group.groupKey]?.pageSize ||
                    DEFAULT_PAGE_SIZE;
                  return {
                    ...row,
                    serialNo: (currentPage - 1) * pageSize + idx + 1,
                  };
                })}
                getRowId={getRowId || ((row) => row.id)}
                aditionalGridProps={{
                  primaryKey: 'id',
                  totalRecords: group.summaryTotal,
                  className: 'rpa-summary-grid',
                  enableCheckbox: false,
                  disableVirtualization: false,
                  showPerPageOptions: false,
                  itemsPerPage:
                    summaryPagination[group.groupKey]?.pageSize ||
                    DEFAULT_PAGE_SIZE,
                  currentPage: summaryPagination[group.groupKey]?.page || 1,
                  paginationMode: 'server',
                  sortingMode: 'client',
                  alwaysShowPagination: true,
                  sortModel: summarySortModel[group.groupKey] || [],
                  onSortModelChange: (model) => {
                    setSummarySortModel((prev) => ({
                      ...prev,
                      [group.groupKey]: model,
                    }));
                    // Reset to first page when sorting changes
                    setSummaryPagination((prev) => ({
                      ...prev,
                      [group.groupKey]: {
                        ...prev[group.groupKey],
                        page: 1,
                      },
                    }));
                  },
                  setItemsPerPage: (pageSize) => {
                    setSummaryPagination((prev) => ({
                      ...prev,
                      [group.groupKey]: {
                        page: 1,
                        pageSize,
                      },
                    }));
                  },
                  onPageChange: (pageNumber, pageSize) => {
                    setSummaryPagination((prev) => ({
                      ...prev,
                      [group.groupKey]: {
                        page: pageNumber,
                        pageSize:
                          pageSize ||
                          prev[group.groupKey]?.pageSize ||
                          DEFAULT_PAGE_SIZE,
                      },
                    }));
                  },
                  getRowHeight: () => 'auto',
                  entityType: 'entries',
                  disableDataGridPagination: true,
                }}
              />
            ) : (
              <Box className="no-data-message-container">
                <Typography
                  className="no-data-message typo-text subhead5_size"
                  data-testid={`grouped-assignment-tables-no-data-available-in-table`}
                >
                  {t('reports.reviewProgressAssignment.noDataAvailableInTable')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Second Table: assignmentDetailsList - Only show if showDetailsTable is true */}
          {showDetailsTable && (
            <Box className="summary-table-wrapper">
              <Box className="assignment-group-header">
                <Typography
                  className="assignment-group-header-title typo-text subhead3_size"
                  data-testid={`grouped-assignment-tables-assignment-details`}
                >
                  {t('reports.reviewProgressAssignment.assignmentDetails')}
                </Typography>
              </Box>
              {group.detailsTotal > 0 ? (
                <ReportsDataGrid
                  loading={false}
                  columns={[
                    {
                      field: 'serialNo',
                      headerName: '',
                      width: 40,
                      flex: 0,
                      isVisible: true,
                    },
                    ...detailsColumns.filter((col) => col.isVisible !== false),
                  ]}
                  rows={group.detailsData.map((row: any, idx: number) => {
                    const currentPage =
                      detailsPagination[group.groupKey]?.page || 1;
                    const pageSize =
                      detailsPagination[group.groupKey]?.pageSize ||
                      DEFAULT_PAGE_SIZE;
                    return {
                      ...row,
                      serialNo: (currentPage - 1) * pageSize + idx + 1,
                    };
                  })}
                  getRowId={getRowId || ((row) => row.id)}
                  aditionalGridProps={{
                    primaryKey: 'id',
                    totalRecords: group.detailsTotal,
                    className: 'rpa-details-grid',
                    enableCheckbox: false,
                    disableVirtualization: false,
                    showPerPageOptions: false,
                    itemsPerPage:
                      detailsPagination[group.groupKey]?.pageSize ||
                      DEFAULT_PAGE_SIZE,
                    currentPage: detailsPagination[group.groupKey]?.page || 1,
                    paginationMode: 'server',
                    sortingMode: 'client',
                    alwaysShowPagination: true,
                    sortModel: detailsSortModel[group.groupKey] || [],
                    onSortModelChange: (model) => {
                      setDetailsSortModel((prev) => ({
                        ...prev,
                        [group.groupKey]: model,
                      }));
                      // Reset to first page when sorting changes
                      setDetailsPagination((prev) => ({
                        ...prev,
                        [group.groupKey]: {
                          ...prev[group.groupKey],
                          page: 1,
                        },
                      }));
                    },
                    setItemsPerPage: (pageSize) => {
                      setDetailsPagination((prev) => ({
                        ...prev,
                        [group.groupKey]: {
                          page: 1,
                          pageSize,
                        },
                      }));
                    },
                    onPageChange: (pageNumber, pageSize) => {
                      setDetailsPagination((prev) => ({
                        ...prev,
                        [group.groupKey]: {
                          page: pageNumber,
                          pageSize:
                            pageSize ||
                            prev[group.groupKey]?.pageSize ||
                            DEFAULT_PAGE_SIZE,
                        },
                      }));
                    },
                    getRowHeight: () => 'auto',
                    entityType: 'entries',
                    disableDataGridPagination: true,
                  }}
                />
              ) : (
                <Box className="no-data-message-container">
                  <Typography
                    className="no-data-message typo-text subhead5_size"
                    data-testid={`grouped-assignment-tables-no-data-available-in-table-text`}
                  >
                    {t(
                      'reports.reviewProgressAssignment.noDataAvailableInTable'
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default GroupedAssignmentTables;
